import { AuthService } from '@/modules/auth/services/auth.service';
import { omitDeep, pick } from '@/lib/helpers';
import Logger from '@/lib/logger';
import TokenStorage from '@/lib/token-storage';

const authService = new AuthService();

export const state = {
  refreshToken: '',
  token: '',
  status: '',
  error: '',
  user: {},
  redirectURL: '',
  disablePrivileges: false
};

export const mutations = {
  SET_REDIRECT_URL(state, payload) {
    state.redirectURL = payload;
  },

  SET_STATUS(state, payload) {
    state.status = payload;
  },

  SET_USER(state, payload) {
    const roles = this.state.coreModule.roles;
    const userRole = roles.find((role) => role.id === payload.role);
    state.user = { ...payload, roleData: userRole };
  },

  SET_TOKENS(state, { token, refreshToken }) {
    state.token = token;
    state.refreshToken = refreshToken;
    TokenStorage.setToken(token);
    TokenStorage.setRefreshToken(refreshToken);
  },

  CLEAR_TOKENS(state) {
    state.token = '';
    state.refreshToken = '';
    TokenStorage.removeToken();
    TokenStorage.removeRefreshToken();
  },

  CLEAR_USER(STATE) {
    STATE.user = null;
  },

  SET_DISABLE_PRIVILEGES(state) {
    state.disablePrivileges = !state.disablePrivileges;
  }
};

export const actions = {
  setRedirectURL: ({ commit }, payload) => {
    commit('SET_REDIRECT_URL', payload);
  },

  getMe: async ({ commit }) => {
    try {
      const { data, status } = await authService.getMe();
      commit('SET_STATUS', status || 'failed');
      commit('SET_USER', omitDeep(data, ['token', 'refreshToken']));
      commit('SET_TOKENS', pick(data, ['token', 'refreshToken']));
      return data;
    } catch (e) {
      Logger.error('Failed to get logged in user', e);
      throw e;
    }
  },

  signIn: async ({ commit }, { email, password }) => {
    try {
      const { data, status } = await authService.signIn(email, password);
      commit('SET_STATUS', status || 'failed');
      commit('SET_TOKENS', pick(data, ['token', 'refreshToken']));
      commit('SET_USER', omitDeep(data, ['token', 'refreshToken']));
      console.log('SIGN IN', data);
      return data;
    } catch (e) {
      Logger.error('sign in failed', e);
      throw e;
    }
  },

  signInWith2fa: async ({ commit }, { email, code, useBackupCode = false }) => {
    try {
      const { data, status } = await authService.signInWith2fa({
        email,
        code,
        useBackupCode
      });
      commit('SET_STATUS', status || 'failed');
      commit('SET_TOKENS', pick(data, ['token', 'refreshToken']));
      commit('SET_USER', omitDeep(data, ['token', 'refreshToken']));
      return data;
    } catch (e) {
      Logger.error('sign in failed', e);
      throw e;
    }
  },

  signOut: async ({ commit }, id) => {
    try {
      await authService.signOut(id);
    } catch (e) {
      Logger.error('sign out failed');
      throw e;
    }
    commit('SET_STATUS', 'Not Authenticated');
    commit('CLEAR_TOKENS');
    commit('CLEAR_USER');
  },

  requestPasswordChange: async ({ commit }, email) => {
    try {
      await authService.requestPasswordChange(email);
      commit('CLEAR_USER');
    } catch (e) {
      Logger.error('request password change failed');
      throw e;
    }
  },

  togglePrivileges({ commit }) {
    commit('SET_DISABLE_PRIVILEGES', true);
  }
};

export const getters = {
  user: (state) => state.user,
  userFullName: (state) => `${state.user?.first_name} ${state.user?.last_name}`,
  userModulePermissionsMap: (state) => {
    // Get the current user's role
    const role = state?.user?.roleObject || {};
    const authorizedModules = new Map();
    for (let i = 0; i < role.permissions.length; i++) {
      const permission = role.permissions[i];

      if (!authorizedModules.has(permission.app)) {
        authorizedModules.set(permission.app.toLowerCase(), []);
      }
      authorizedModules.get(permission.app.toLowerCase()).push(permission.name);
    }
    return authorizedModules;
  },

  isAuthenticated: (state) => !!state.token, // This should validated based on time - on computed time
  authStatus: (state) => state.status,
  getRedirectURL: (state) => state.redirectURL, //returns the url that the user was on before log out
  getUserAvatar(state) {
    if (!state.user) return '';
    if (!state.user.avatar) return '';
    return state.user.avatar;
  },

  /**
   * This getter will check if the user has access to a certain permission
   * Pass in the permission name as a string
   */
  roleAccess: (state) => (permission, app) => {
    // Get the current user's role
    const role = state?.user?.roleObject || {};
    // Get the permissions
    const userPermissions = role?.permissions || [];

    if (Array.isArray(permission)) {
      return permission.every((perm) =>
        userPermissions.some(
          (up) =>
            up?.name?.toLowerCase().trim() === perm.toLowerCase().trim() &&
            (!app || up?.app?.toLowerCase().trim() === app.toLowerCase().trim())
        )
      );
    }
    // Check if the permission is in the permissions array
    const found = userPermissions.find(
      (p) =>
        p?.name?.toLowerCase().trim() === permission?.toLowerCase().trim() &&
        (!app || p?.app?.toLowerCase().trim() === app.toLowerCase().trim())
    );
    // Return the boolean value
    return found ? true : false;
  },

  moduleAccess: (state) => (app) => {
    const role = state?.user?.roleObject || {};
    const userPermissions = role?.permissions || [];
    const apps = Array.isArray(app) ? app : [app];

    return apps.some((appName) =>
      userPermissions.some(
        (permission) =>
          permission?.app?.toLowerCase().trim() ===
          appName?.toLowerCase().trim()
      )
    );
  }
};
